import { FC, useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';

import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { useAppDispatch } from '../../../hooks/redux';

import { useMobileToolSheet } from '../../meet/tools-panel/mobile-tool-sheet';
import { IColorCodeAnalysisResponse, useSendColorCodeAnalysisMutation } from '../../../services/colorCode.api';

import { IJournalResult } from '../../../store/types';
import { Heading, ArrowButton, Pressable, Button } from '../../../shared/ui';

import { CountdownTimer } from './countdown-timer';
import { ColorCards } from './color-cards';

interface IColorCodePsyProps {
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  onToolActivated?: (tool: string) => void;
  onActiveTabChange?: (hasActiveTab: boolean) => void;
  sendColorCodeOpen?: () => void;
  clientColorInbound?: { nonce: number; selectedColors: string[] } | null;
  onClientColorInboundConsumed?: () => void;
  /** Мобильный 1:1: панель внутри нижней шторки (заголовок рисует шторка) */
  mobile?: boolean;
}

const COLORS = ['#D90018', '#42AA02', '#93532C', '#FFCD0B', '#51007E', '#013C7E', '#817F85', '#010001'];
const COLOR_NAME_BY_HEX: Record<string, string> = {
  '#D90018': 'RED',
  '#42AA02': 'GREEN',
  '#93532C': 'BROWN',
  '#FFCD0B': 'YELLOW',
  '#51007E': 'PURPLE',
  '#013C7E': 'BLUE',
  '#817F85': 'GRAY',
  '#010001': 'BLACK',
};

type AnalysisTabKey = 'diagnostics' | 'techniques' | 'hintQuestions';
type SliderTabKey = Exclude<AnalysisTabKey, 'diagnostics'>;
const COUNTDOWN_SECONDS = 30;
const COUNTDOWN_DURATION_MS = COUNTDOWN_SECONDS * 1000;

const ANALYSIS_TABS: { key: AnalysisTabKey; label: string; mobileLabel: string }[] = [
  { key: 'diagnostics', label: 'Заключение', mobileLabel: 'Заключение' },
  { key: 'hintQuestions', label: 'Вопросы-подсказки', mobileLabel: 'Вопросы' },
  { key: 'techniques', label: 'Техники', mobileLabel: 'Техники' },
];

/** Ряд цветовых карточек на всю ширину шторки; numbered — порядковые номера выбора клиента */
const ColorSwatchRow: FC<{ colors: string[]; numbered?: boolean }> = ({ colors, numbered = false }) => (
  <div className="flex gap-2">
    {colors.map((color, idx) => (
      <div
        key={`${color}-${idx}`}
        className="relative aspect-[3/4] min-w-0 flex-1 rounded-control"
        style={{ backgroundColor: color }}
      >
        {numbered && (
          <span className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-black/55 text-micro font-medium text-white">
            {idx + 1}
          </span>
        )}
      </div>
    ))}
  </div>
);

export const ColorCodePsy: FC<IColorCodePsyProps> = ({
  result,
  setResult,
  onToolActivated,
  onActiveTabChange,
  sendColorCodeOpen,
  clientColorInbound,
  onClientColorInboundConsumed,
  mobile = false,
}) => {
  const dispatch = useAppDispatch();
  const sheet = useMobileToolSheet();
  const [sendColorCodeAnalysis] = useSendColorCodeAnalysisMutation();
  const [active, setActive] = useState(false);
  const [clientColors, setClientColors] = useState<string[] | null>(null);
  const [activeTab, setActiveTab] = useState<AnalysisTabKey | null>(null);
  const [analysisData, setAnalysisData] = useState<IColorCodeAnalysisResponse | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_DURATION_MS);
  const [isCountdownExpired, setIsCountdownExpired] = useState(false);
  const [sliderIndexes, setSliderIndexes] = useState<Record<SliderTabKey, number>>({
    techniques: 0,
    hintQuestions: 0,
  });

  const isProcessingRef = useRef(false);
  const lastClientColorsRef = useRef<string | null>(null);
  const countdownFrameRef = useRef<number | null>(null);
  const countdownStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (!clientColors?.length) {
      isProcessingRef.current = false;
      lastClientColorsRef.current = null;
      setAnalysisData(null);
      return;
    }

    // Создаем уникальный ключ из цветов (с учетом порядка, так как порядок важен для анализа)
    const colorsKey = JSON.stringify(clientColors);

    // Если это тот же самый массив цветов (не изменился), и мы уже обрабатываем - не отправляем запрос
    if (lastClientColorsRef.current === colorsKey && isProcessingRef.current) {
      return;
    }

    // Если цвета изменились - это новый выбор клиента, всегда отправляем новый запрос
    // Сбрасываем флаги и начинаем обработку
    lastClientColorsRef.current = colorsKey;
    isProcessingRef.current = true;
    let isMounted = true;

    const sendAnalysis = async () => {
      setIsAnalysisLoading(true);
      setAnalysisError(null);
      setAnalysisData(null);
      setActiveTab('diagnostics');
      setSliderIndexes({ techniques: 0, hintQuestions: 0 });

      try {
        const normalizedColors = clientColors.map((color) => {
          const key = color.toUpperCase();

          return COLOR_NAME_BY_HEX[key] ?? key;
        });
        const analysisResult = await sendColorCodeAnalysis({ color_cards: normalizedColors }).unwrap();
        if (isMounted) {
          setAnalysisData(analysisResult);

          // Сохраняем анализ в result
          if (setResult) {
            setResult((prev) => {
              const colorCodeArray = Array.isArray(prev.color_code) ? prev.color_code : [];
              const updatedColorCode = [...colorCodeArray];
              // Находим последнюю запись с такими же цветами
              const lastIndex = updatedColorCode.length - 1;
              if (lastIndex >= 0) {
                const lastEntry = updatedColorCode[lastIndex];
                // Проверяем, что цвета совпадают
                const colorsMatch =
                  lastEntry.colors.length === clientColors.length &&
                  lastEntry.colors.every((color, idx) => color === clientColors[idx]);

                if (colorsMatch) {
                  updatedColorCode[lastIndex] = {
                    ...lastEntry,
                    analysis: {
                      diagnostics: analysisResult.diagnostics,
                      techniques: analysisResult.techniques,
                      hintQuestions: analysisResult.hintQuestions,
                    },
                  };
                }
              }

              return {
                ...prev,
                color_code: updatedColorCode,
              };
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          setAnalysisError('Не удалось получить анализ. Попробуйте ещё раз.');
        }
        console.error('Failed to send color-code analysis request', error);
      } finally {
        if (isMounted) {
          setIsAnalysisLoading(false);
          isProcessingRef.current = false;
        }
      }
    };

    sendAnalysis();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientColors]);

  useEffect(() => {
    const durationMs = COUNTDOWN_DURATION_MS;

    const stopCountdown = () => {
      if (countdownFrameRef.current !== null) {
        cancelAnimationFrame(countdownFrameRef.current);
        countdownFrameRef.current = null;
      }
      countdownStartRef.current = null;
    };

    const startCountdown = () => {
      stopCountdown();
      setRemainingMs(durationMs);
      setIsCountdownExpired(false);
      const tick = (timestamp: number) => {
        if (countdownStartRef.current === null) {
          countdownStartRef.current = timestamp;
        }
        const elapsed = timestamp - countdownStartRef.current;
        const remaining = Math.max(0, durationMs - elapsed);
        setRemainingMs(remaining);
        if (remaining > 0 && isAnalysisLoading) {
          countdownFrameRef.current = requestAnimationFrame(tick);
        } else {
          stopCountdown();
          setIsCountdownExpired(remaining <= 0);
        }
      };

      countdownFrameRef.current = requestAnimationFrame(tick);
    };

    if (isAnalysisLoading) {
      startCountdown();
    } else {
      stopCountdown();
      setRemainingMs(durationMs);
      setIsCountdownExpired(false);
    }

    return () => {
      stopCountdown();
    };
  }, [isAnalysisLoading]);

  const handleStartTest = () => {
    sendColorCodeOpen?.();
    dispatch(setActiveTool(PsyToolsId.COLOR_CODE));
    onToolActivated?.('color_code');
    setActive(true);
    // Мобильный: убираем шторку, чтобы видеть клиента, пока он расставляет цвета
    sheet?.collapse();
  };

  useEffect(() => {
    if (!clientColorInbound?.selectedColors?.length) {
      return;
    }

    // Посылка живёт в meet.tsx дольше этой панели: без сброса она переиграется при следующем открытии.
    onClientColorInboundConsumed?.();
    if (!active) {
      return;
    }

    const selectedColors = clientColorInbound.selectedColors;
    setClientColors(selectedColors);
    // Мобильный: клиент выбрал цвета — раскрываем шторку с результатом и анализом
    sheet?.expand();

    if (selectedColors.length > 0 && setResult) {
      const data = {
        date_time: new Date().toISOString(),
        colors: selectedColors,
        comment: '',
      };

      setResult((prev) => ({
        ...prev,
        color_code: Array.isArray(prev.color_code) ? [...prev.color_code, data] : [data],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- обрабатываем только новую посылку по nonce
  }, [clientColorInbound?.nonce]);

  const handleTabClick = (tabKey: AnalysisTabKey) => {
    setActiveTab(tabKey);
  };

  useEffect(() => {
    onActiveTabChange?.(activeTab !== null);
  }, [activeTab, onActiveTabChange]);

  const renderAnalysisContent = () => {
    if (!activeTab) return null;

    if (isAnalysisLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full flex-1 text-center gap-4">
          <CountdownTimer remainingMs={remainingMs} isExpired={isCountdownExpired} durationMs={COUNTDOWN_DURATION_MS} />
          <p className="text-ink-subtle text-body-l-strong">ИИ анализирует выбранные карты…</p>
          {isCountdownExpired && <p className="text-primary text-ui-strong">Подождите еще чуть-чуть</p>}
        </div>
      );
    }

    if (analysisError) {
      return (
        <div className="flex items-center justify-center h-full flex-1 text-center text-primary font-medium">
          {analysisError}
        </div>
      );
    }

    if (!analysisData) {
      return (
        <div className="flex items-center justify-center h-full flex-1 text-center text-ink-subtle">
          Данные пока недоступны.
        </div>
      );
    }

    if (activeTab === 'diagnostics') {
      return (
        <div className="flex flex-col gap-4 pr-2">
          {analysisData.diagnostics.length ? (
            analysisData.diagnostics.map((paragraph, idx) => (
              <div
                key={`diagnostic-${idx}`}
                className={classNames('font-normal leading-relaxed', mobile ? 'text-body-l' : 'text-display-s')}
              >
                <ReactMarkdown
                  components={{
                    p: (paragraphProps) => <p className="mt-0 mb-2 last:mb-0" {...paragraphProps} />,
                  }}
                >
                  {paragraph}
                </ReactMarkdown>
              </div>
            ))
          ) : (
            <p className="text-ui text-ink-subtle">Нет данных для отображения.</p>
          )}
        </div>
      );
    }

    const targetTab: SliderTabKey = activeTab;
    const items = targetTab === 'techniques' ? analysisData.techniques : analysisData.hintQuestions;
    const currentIndex = sliderIndexes[targetTab] ?? 0;

    if (!items.length) {
      return <p className="text-ui text-ink-subtle">Нет данных для отображения.</p>;
    }

    const total = items.length;
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < total - 1;

    const updateIndex = (idx: number) => {
      setSliderIndexes((prev) => ({ ...prev, [targetTab]: idx }));
    };

    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto pr-2">
          <div
            className={classNames(
              'font-normal leading-relaxed break-words min-w-0',
              mobile ? 'text-body-l' : 'text-display-s pl-2',
            )}
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            <ReactMarkdown
              components={{
                p: (paragraphProps) => (
                  <p
                    className="mt-0 mb-2 last:mb-0 break-words"
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                    {...paragraphProps}
                  />
                ),
                ol: (olProps) => (
                  <ol className="list-decimal pl-8" style={{ listStylePosition: 'outside' }} {...olProps} />
                ),
                li: (liProps) => (
                  <li
                    className="break-words"
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word', display: 'list-item' }}
                    {...liProps}
                  />
                ),
              }}
            >
              {items[currentIndex]}
            </ReactMarkdown>
          </div>
        </div>

        <div
          className={classNames('flex items-center justify-center flex-shrink-0', mobile ? 'mt-4 gap-4' : 'mt-8 gap-6')}
        >
          <ArrowButton
            direction="prev"
            label="Предыдущий пункт"
            disabled={!canGoPrev}
            onClick={() => canGoPrev && updateIndex(currentIndex - 1)}
          />

          <div className={classNames('flex items-center', mobile ? 'gap-3' : 'gap-6')}>
            {items.map((_, idx) => {
              const isActive = idx === currentIndex;

              return (
                <Pressable
                  key={`${targetTab}-dot-${idx}`}
                  onClick={() => updateIndex(idx)}
                  aria-label={`Перейти к пункту ${idx + 1}`}
                  className={classNames(
                    'rounded-full transition-colors',
                    isActive ? 'bg-ink-subtle' : 'bg-line hover:bg-line-strong',
                  )}
                  style={{ width: isActive ? 20 : 14, height: isActive ? 20 : 14 }}
                />
              );
            })}
          </div>

          <ArrowButton
            direction="next"
            label="Следующий пункт"
            disabled={!canGoNext}
            onClick={() => canGoNext && updateIndex(currentIndex + 1)}
          />
        </div>
      </div>
    );
  };

  if (mobile) {
    const hasClientColors = Boolean(active && clientColors);

    return (
      <div className="flex w-full flex-col gap-3 text-white">
        {!hasClientColors ? (
          <>
            <div className="text-ui leading-relaxed text-white/90">
              Предложение клиенту:
              <br />
              «Во время теста вам будут показаны карточки с различными цветами. Ваша задача — расставить карточки
              начиная с цвета, который вам нравится больше других в данный момент времени, и далее выбирать самый
              предпочитаемый из оставшихся. На последнем месте будет цвет, с которым вы чувствуете наименьшую симпатию
              по сравнению с остальными. Не пытайтесь ассоциировать цвета ни с чем посторонним — например, с цветом
              одежды или интерьера»
            </div>
            <ColorSwatchRow colors={COLORS} />
            <div className="sticky bottom-[-1rem] z-40 -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              <Button size="lg" fullWidth loading={active && !clientColors} onClick={handleStartTest}>
                {active && !clientColors ? 'Клиент думает…' : 'Начать тест'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="mb-2 text-ui text-white/80">Цветовой выбор клиента</p>
              <ColorSwatchRow colors={clientColors ?? []} numbered />
            </div>

            <div role="tablist" className="grid grid-cols-3 gap-1 rounded-full bg-black/25 p-1">
              {ANALYSIS_TABS.map(({ key, mobileLabel }) => (
                <Pressable
                  key={key}
                  role="tab"
                  aria-selected={activeTab === key}
                  onClick={() => handleTabClick(key)}
                  className={classNames(
                    'rounded-full px-2 py-2 text-ui-strong transition-colors',
                    activeTab === key ? 'bg-accent text-ink' : 'text-white/80',
                  )}
                >
                  {mobileLabel}
                </Pressable>
              ))}
            </div>

            {activeTab && (
              <div className="flex min-h-[220px] flex-col rounded-card border border-accent bg-white p-4 text-ink">
                {renderAnalysisContent()}
              </div>
            )}

            <div className="sticky bottom-[-1rem] z-40 -mx-4 flex gap-2 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              <Button size="md" variant="ghostInverse" fullWidth onClick={() => sheet?.restart()}>
                Новый тест
              </Button>
              <Button size="md" fullWidth onClick={() => sheet?.finish()}>
                Завершить
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {!(active && clientColors) && (
        <div className="w-[302px] min-w-[302px] h-full flex flex-col text-white rounded-l-control overflow-y-auto white-scroll p-6">
          <>
            <p className="text-display-s mb-4">Цветовой код</p>
            <div className="text-body-l leading-relaxed">
              Предложение клиенту:
              <br />
              «Во время теста вам будут показаны карточки с различными цветами.
              <br />
              <br />
              Ваша задача –расставить карточки начиная с цвета, который вам нравится больше других в данный момент
              времени и далее выбирать самый предпочитаемый из оставшихся. <br />
              <br /> На последнем месте будет цвет, с которым вы чувствуете наименьшую симпатию по сравнению с
              остальными. <br />
              <br /> Не пытайтесь ассоциировать цвета ни с чем посторонним, – например, с цветом одежды или интерьера»
            </div>

            <ColorCards colors={COLORS} />

            <div className="mt-auto pt-6">
              <Button size="lg" fullWidth loading={active && !clientColors} onClick={handleStartTest}>
                {active && !clientColors ? 'Клиент думает…' : 'Начать тест'}
              </Button>
            </div>
          </>{' '}
        </div>
      )}

      {active && clientColors && (
        <div className="flex flex-col justify-between h-full w-[302px] min-w-[302px] text-white rounded-l-control p-6">
          <div>
            <Heading tone="inverse" size="lg" className="mb-6">
              Цветовой код
            </Heading>
            <p className="text-body-xl mb-8">Цветовой выбор Клиента</p>
            <ColorCards colors={clientColors} />
          </div>

          <div className="mt-6 flex flex-col gap-3 font-normal">
            {ANALYSIS_TABS.map(({ key, label }) => (
              <Pressable
                key={key}
                aria-pressed={activeTab === key}
                onClick={() => handleTabClick(key)}
                className={classNames(
                  'w-full py-3 rounded-control text-body-l-strong transition-all',
                  activeTab === key ? 'bg-accent text-ink' : 'bg-ink-subtle text-white hover:bg-ink-subtle',
                )}
              >
                {label}
              </Pressable>
            ))}
          </div>
        </div>
      )}

      {activeTab && (
        <div className="flex-1 w-[616px] bg-white border-1 border-accent text-ink rounded-card p-8 pr-16 leading-relaxed flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">{renderAnalysisContent()}</div>
        </div>
      )}
    </div>
  );
};
